import React from "react";
import { Link, useLocation } from "react-router-dom";
import Breadcrumbs from "../navigation/Breadcrumbs";
import { buildServiceBreadcrumbs } from "../navigation/breadcrumbUtils";
import { getBreadcrumbSchema } from "../navigation/breadcrumbSchema";
import SEO from "../seo/SEO";
import "../HRservices/HRServicesPage.css";
import "./HealthServicePage.css";

const HEALTH_SERVICE_DETAILS = {
  medicalCoding: {
    title: "Medical Coding",
    intro:
      "BlackRoth converts clinical documentation into precise, standardized medical codes that support compliant healthcare records and accurate reimbursement.",
    overview:
      "Medical coding translates diagnoses, procedures, treatments, and medical supplies into recognized code sets. Our coding specialists help hospitals, clinics, and physician groups maintain consistent and audit-ready clinical data.",
    capabilities:
      "Our team reviews patient charts, selects ICD-10-CM, CPT, and HCPCS codes, applies specialty guidelines, checks code sequencing, and flags documentation that requires clarification.",
    services: [
      "Inpatient, outpatient, and professional coding",
      "ICD-10-CM diagnosis code assignment",
      "CPT and HCPCS procedure coding",
      "Specialty-specific coding support",
      "Coding compliance audits and quality reviews",
      "Clinical documentation gap identification",
    ],
    process:
      "Clinical records are securely assigned by specialty and encounter type. Coders review the documentation, apply the required codes, complete quality checks, and return queries whenever the record does not fully support a code.",
    value:
      "Accurate coding improves data integrity, supports regulatory compliance, reduces coding-related rework, and gives providers dependable information for reimbursement and clinical reporting.",
  },
  medicalBilling: {
    title: "Medical Billing",
    intro:
      "BlackRoth manages the day-to-day billing transactions that turn completed healthcare services into accurate payer and patient charges.",
    overview:
      "Medical billing focuses on preparing charges, creating claim forms, transmitting them through the correct channels, recording payments, and maintaining current patient account balances.",
    capabilities:
      "Our billing specialists handle demographic entry, charge entry, claim-format validation, clearinghouse submission, remittance processing, payment allocation, and patient statement support.",
    services: [
      "Patient demographic and insurance data entry",
      "Charge entry and claim form preparation",
      "Pre-submission billing validation",
      "Electronic and paper claim transmission",
      "Insurance and patient payment posting",
      "Billing statements and balance support",
    ],
    process:
      "Approved codes and charges enter the billing queue, where required patient and payer fields are verified. Claims are formatted and transmitted, remittance files are processed, and payments or adjustments are posted to the correct account.",
    value:
      "A disciplined billing workflow reduces administrative delays, keeps account balances accurate, improves payment-posting consistency, and creates a clearer billing experience for patients and staff.",
  },
  revenueCycleManagement: {
    title: "Revenue Cycle Management (RCM)",
    intro:
      "BlackRoth helps healthcare leaders improve financial performance by coordinating and measuring the complete revenue journey from patient access to final account resolution.",
    overview:
      "RCM is a broader operational discipline than billing. It connects front-office activity, clinical handoffs, payer reimbursement, collections, and financial analysis to reveal where revenue is delayed or lost.",
    capabilities:
      "We assess workflows and performance data, monitor payer and aging trends, identify revenue leakage and underpayments, establish improvement targets, and provide leadership-level KPI reporting.",
    services: [
      "End-to-end revenue cycle performance assessment",
      "Payer contract and reimbursement trend analysis",
      "Revenue leakage and underpayment identification",
      "Aged receivables recovery strategy",
      "Workflow optimization and control design",
      "Financial KPI dashboards and executive reporting",
    ],
    process:
      "We establish baseline metrics, map revenue handoffs, locate high-impact gaps, and create a prioritized improvement plan. Progress is measured through clean-claim rate, denial rate, collection rate, days in accounts receivable, and reimbursement turnaround.",
    value:
      "Healthcare organizations gain more predictable cash flow, stronger accountability between teams, reduced revenue leakage, healthier receivables, and reliable insight for financial decision-making.",
  },
  claimsManagement: {
    title: "Claims Management",
    intro:
      "BlackRoth provides structured healthcare claims management support to help providers move claims accurately and efficiently from initial preparation through payer resolution.",
    overview:
      "Our claims management service focuses on claim quality, status visibility, payer responses, and timely resolution. It gives healthcare teams a controlled workflow for handling accepted, rejected, pending, and denied claims.",
    capabilities:
      "We validate claim information, monitor submission status, interpret payer responses, investigate rejections, coordinate corrections, manage appeals, and maintain clear claim-level documentation.",
    services: [
      "Claim data validation and readiness checks",
      "Electronic claim status monitoring",
      "Rejection investigation and correction",
      "Payer correspondence and follow-up",
      "Denial appeal preparation and tracking",
      "Claims inventory and resolution reporting",
    ],
    process:
      "Claims enter a monitored work queue and are reviewed against payer and submission requirements. Our team tracks acknowledgements and responses, assigns exceptions for action, documents every follow-up, and escalates unresolved claims according to agreed timelines.",
    value:
      "Providers gain better control of outstanding claims, quicker exception handling, fewer missed follow-ups, stronger appeal documentation, and clearer insight into claim outcomes and payer behavior.",
  },
  insuranceEligibilityVerification: {
    title: "Insurance Eligibility Verification",
    intro:
      "BlackRoth verifies patient insurance coverage before care is delivered, helping healthcare teams begin each visit with clear and reliable benefit information.",
    overview:
      "Insurance eligibility verification confirms whether a policy is active and identifies the benefits, limitations, and patient responsibilities that apply to a scheduled service. This front-office step helps prevent avoidable coverage surprises and registration delays.",
    capabilities:
      "Our verification specialists check payer portals and approved channels for policy status, effective dates, plan type, covered services, copayments, deductibles, coinsurance, referral rules, and authorization requirements.",
    services: [
      "Active policy and coverage-date confirmation",
      "Patient demographic and member ID validation",
      "Copayment, deductible, and coinsurance review",
      "Service-specific benefit verification",
      "Referral and prior-authorization requirement checks",
      "Eligibility exception reporting before appointments",
    ],
    process:
      "Upcoming appointments are placed in a verification queue based on service date and priority. Patient and policy details are validated, benefits are checked with the payer, findings are documented, and coverage exceptions are returned to the care team for action before the visit.",
    value:
      "Providers gain fewer eligibility-related claim issues, more accurate upfront estimates, smoother patient registration, better collection readiness, and additional time to resolve coverage concerns before care begins.",
  },
};

const AnimatedTitle = ({ children }) => (
  <h1 className="health-service-title" aria-label={children}>
    <span aria-hidden="true">
      {children.split("").map((character, index) => (
        <span
          className={`health-service-title__letter${
            character === " " ? " health-service-title__space" : ""
          }`}
          style={{ "--letter-index": index }}
          key={`${character}-${index}`}
        >
          {character === " " ? "\u00a0" : character}
        </span>
      ))}
    </span>
  </h1>
);

const HealthServicePage = ({ serviceKey }) => {
  const location = useLocation();
  const service = HEALTH_SERVICE_DETAILS[serviceKey];
  const breadcrumbItems = buildServiceBreadcrumbs("health", service.title);

  return (
    <div className="standard-page-wrapper health-service-page">
      <SEO schema={getBreadcrumbSchema(breadcrumbItems)} />
      <main className="standard-page-container">
        <Breadcrumbs items={breadcrumbItems} />

        <div className="standard-back-link">
          <Link to="/" state={{ from: location.pathname }}>
            ← Back to Home
          </Link>
        </div>

        <header className="standard-page-hero">
          <AnimatedTitle key={service.title}>{service.title}</AnimatedTitle>
          <p>{service.intro}</p>
        </header>

        <section className="standard-page-section">
          <h2>Overview</h2>
          <p>{service.overview}</p>
        </section>

        <section className="standard-page-section">
          <h2>Core Capabilities</h2>
          <p>{service.capabilities}</p>
        </section>

        <section className="standard-page-section">
          <h2>Services We Offer</h2>
          <ul className="standard-service-list">
            {service.services.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>

        <section className="standard-page-section">
          <h2>Our Process</h2>
          <p>{service.process}</p>
        </section>

        <section className="standard-page-section">
          <h2>Business Value</h2>
          <p>{service.value}</p>
        </section>
      </main>
    </div>
  );
};

export default HealthServicePage;
